import { useState } from "react";
import { Card } from "../ui/Card";
import { Input } from "../ui/Input";
import { Button } from "../ui/Button";
import {
  Search,
  Filter,
  ArrowUpDown,
  MoreHorizontal,
  Edit2,
  Trash2,
  Eye,
  CheckCircle2,
  XCircle,
  Layers,
  Flame,
} from "lucide-react";
import { TableActionMenu } from "../ui/TableActionMenu";
import { useAuth } from "../../contexts/AuthContext";

export interface Course {
  _id: string;
  title: string;
  slug: string;
  description: string;
  thumbnail?: string;
  instructor?:
    | string
    | {
        _id: string;
        user?: {
          firstName: string;
          lastName: string;
          profilePicture?: string;
        };
      };
  trade?:
    | string
    | {
        _id: string;
        name: string;
      };
  level: string;
  language: string;
  price: number;
  discountPrice?: number;
  isFree: boolean;
  isPublished: boolean;
  status: string;
  totalModules: number;
  totalLessons: number;
  enrollmentCount: number;
  averageRating: number;
  createdAt: string;
  isTrending?: boolean;
  orderIndex?: number;
  duration?: number;
  accessDurationDays?: number | null;
}

interface CourseTableProps {
  courses: Course[];
  description?: string;
  isLoading?: boolean;
  totalItems: number;
  currentPage: number;
  pageSize: number;
  searchTerm: string;
  statusFilter: string;
  sortField: string;
  sortOrder: "asc" | "desc";
  onSearchChange: (value: string) => void;
  onStatusChange: (status: string) => void;
  onSortChange: (field: string, order: "asc" | "desc") => void;
  onPageChange: (page: number) => void;
  onAddClick?: () => void;
  onEditClick: (course: Course) => void;
  onDeleteClick: (course: Course) => void;
  onViewClick: (course: Course) => void;
  onManageStructure: (course: Course) => void;
  onTogglePublish?: (course: Course) => void;
  onReorderClick?: () => void;
  onResyncStats?: () => void;
  extraHeaderActions?: React.ReactNode;
}

export function CourseTable({
  courses,
  description = "Manage your courses, pricing, and content structure.",
  isLoading,
  totalItems,
  currentPage,
  pageSize,
  searchTerm,
  statusFilter,
  sortField,
  sortOrder,
  onSearchChange,
  onStatusChange,
  onSortChange,
  onPageChange,
  onAddClick,
  onEditClick,
  onDeleteClick,
  onViewClick,
  onManageStructure,
  onTogglePublish,
  onReorderClick,
  onResyncStats,
  extraHeaderActions,
}: CourseTableProps) {
  const { hasPermission } = useAuth();
  const [activeMenu, setActiveMenu] = useState<{
    id: string;
    position: { top: number; right: number };
  } | null>(null);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);

  const formatDate = (dateString: string) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatPrice = (price: number, isFree: boolean) => {
    if (isFree) return "Free";
    return `$${price.toFixed(2)}`;
  };

  const handleSortClick = (field: string) => {
    const newOrder =
      sortField === field && sortOrder === "asc" ? "desc" : "asc";
    onSortChange(field, newOrder);
  };

  const getStatusBadge = (status: string, isPublished: boolean) => {
    if (isPublished || status === "published") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#E8F5EE] text-[#1B8754]">
          <CheckCircle2 size={12} className="mr-1" />
          Published
        </span>
      );
    }
    if (status === "archived") {
      return (
        <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#F0F0F0] text-[#666666]">
          Archived
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-3 py-1 rounded-full text-[12px] font-semibold bg-[#FFF8E6] text-[#FFB020]">
        <XCircle size={12} className="mr-1" />
        Draft
      </span>
    );
  };

  const getLevelBadge = (level: string) => {
    const colors: Record<string, string> = {
      beginner: "bg-green-100 text-green-700",
      intermediate: "bg-blue-100 text-blue-700",
      advanced: "bg-purple-100 text-purple-700",
    };
    return (
      <span
        className={`px-2 py-0.5 rounded text-[11px] font-medium ${
          colors[level] || "bg-gray-100 text-gray-700"
        }`}
      >
        {level.charAt(0).toUpperCase() + level.slice(1)}
      </span>
    );
  };

  const totalPages = Math.ceil(totalItems / pageSize);

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-1">
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">
          Courses
        </h2>
        <p className="text-gray-500">{description}</p>
      </div>

      <div className="flex flex-col xl:flex-row justify-between items-start xl:items-center gap-4">
        {/* Search input */}
        <div className="w-full xl:w-[408px]">
          <Input
            placeholder="Search courses"
            icon={<Search size={20} />}
            className="bg-white w-full"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
          />
        </div>

        {/* Filter buttons and primary action */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full xl:w-auto">
          <div className="grid grid-cols-2 sm:flex items-center gap-2 w-full sm:w-auto">
            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsFilterOpen(!isFilterOpen);
                  setIsSortOpen(false);
                }}
              >
                <Filter size={16} /> Filter
              </Button>
              {isFilterOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsFilterOpen(false)}
                  />
                  <div className="absolute left-0 sm:left-auto sm:right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[120px] w-full sm:w-max animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        onStatusChange("all");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "all" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      All Status
                    </button>
                    <button
                      onClick={() => {
                        onStatusChange("published");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "published"
                          ? "bg-gray-50 font-bold text-green-600"
                          : ""
                      }`}
                    >
                      Published
                    </button>
                    <button
                      onClick={() => {
                        onStatusChange("draft");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "draft"
                          ? "bg-gray-50 font-bold text-yellow-600"
                          : ""
                      }`}
                    >
                      Draft
                    </button>
                    <button
                      onClick={() => {
                        onStatusChange("archived");
                        setIsFilterOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        statusFilter === "archived"
                          ? "bg-gray-50 font-bold text-gray-600"
                          : ""
                      }`}
                    >
                      Archived
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="relative w-full sm:w-auto">
              <Button
                variant="outline"
                size="sm"
                className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto justify-center touch-target"
                onClick={() => {
                  setIsSortOpen(!isSortOpen);
                  setIsFilterOpen(false);
                }}
              >
                <ArrowUpDown size={16} /> Sort By
              </Button>
              {isSortOpen && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setIsSortOpen(false)}
                  />
                  <div className="absolute right-0 top-full mt-1 z-20 bg-white border border-gray-100 rounded-lg shadow-lg p-1 min-w-[160px] animate-in fade-in zoom-in duration-150">
                    <button
                      onClick={() => {
                        onSortChange("createdAt", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "createdAt" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Date Created
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("orderIndex", "asc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "orderIndex"
                          ? "bg-gray-100 font-bold"
                          : ""
                      }`}
                    >
                      Custom Order
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("title", "asc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "title" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Title
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("enrollmentCount", "desc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "enrollmentCount"
                          ? "bg-gray-100 font-bold"
                          : ""
                      }`}
                    >
                      Enrollments
                    </button>
                    <button
                      onClick={() => {
                        onSortChange("price", "asc");
                        setIsSortOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-xs rounded hover:bg-gray-50 ${
                        sortField === "price" ? "bg-gray-100 font-bold" : ""
                      }`}
                    >
                      Price
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>

          {onResyncStats && (
            <Button
              variant="outline"
              size="sm"
              className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto touch-target mb-2 sm:mb-0"
              onClick={onResyncStats}
            >
              <Flame size={16} className="text-amber-500" /> Resync stats
            </Button>
          )}

          {onReorderClick && (
            <Button
              variant="outline"
              size="sm"
              className="h-[44px] sm:h-[39px] px-3 w-full sm:w-auto touch-target mb-2 sm:mb-0"
              onClick={onReorderClick}
            >
              <ArrowUpDown size={16} className="rotate-90" /> Reorder courses
            </Button>
          )}

          {/* Extra header actions (e.g. Export CSV) + primary button */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
            {extraHeaderActions}
            {hasPermission('courses:create') && (
              <Button
                variant="primary"
                size="lg"
                className="w-full sm:w-[180px] whitespace-nowrap h-[48px] sm:h-[44px] touch-target"
                onClick={onAddClick}
              >
                Add new course
              </Button>
            )}
          </div>
        </div>
      </div>

      <Card className="p-0 border shadow-sm w-full h-full flex flex-col">
        {/* Single Scrollable Table View */}
        <div className="overflow-x-auto">
          <table className="min-w-[1200px] w-full text-left text-sm border-separate border-spacing-0">
            <thead className="bg-gray-50 border-b border-gray-100 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[30%]">
                  Course Info
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[15%]">
                  Trade
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[10%]">
                  Level
                </th>
                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[10%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSortClick("price")}
                >
                  Price{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "price"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[10%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSortClick("enrollmentCount")}
                >
                  Stats{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "enrollmentCount"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[10%]">
                  Duration
                </th>
                <th className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[12%]">
                  Status
                </th>
                <th
                  className="px-6 py-4 font-semibold text-gray-900 bg-gray-50 border-b border-gray-100 w-[10%] cursor-pointer hover:text-indigo-600 transition-colors"
                  onClick={() => handleSortClick("createdAt")}
                >
                  Created{" "}
                  <ArrowUpDown
                    size={14}
                    className={`inline ml-1 ${
                      sortField === "createdAt"
                        ? "text-indigo-600"
                        : "text-gray-400"
                    }`}
                  />
                </th>
                <th className="px-6 py-4 w-[3%] bg-gray-50 border-b border-gray-100"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    Loading courses...
                  </td>
                </tr>
              ) : courses.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-6 py-8 text-center text-gray-500"
                  >
                    No courses found
                  </td>
                </tr>
              ) : (
                courses.map((course) => (
                  <tr
                    key={course._id}
                    className="hover:bg-gray-50/50 transition-colors"
                  >
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-3">
                        {course.thumbnail ? (
                          <img
                            src={course.thumbnail}
                            alt={course.title}
                            className="w-14 h-10 rounded object-cover"
                          />
                        ) : (
                          <div className="w-14 h-10 rounded bg-indigo-100 flex items-center justify-center text-indigo-700 font-bold text-xs">
                            {course.title.substring(0, 2).toUpperCase()}
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#102040] flex items-center gap-2">
                            {course.title}
                            {course.isTrending && (
                              <Flame
                                size={14}
                                className="text-orange-500 fill-orange-500 shrink-0"
                                aria-label="Trending"
                              />
                            )}
                          </p>
                          <p className="text-xs text-gray-500">
                            {typeof course.instructor === "object" &&
                              course.instructor?.user?.firstName}{" "}
                            {typeof course.instructor === "object" &&
                              course.instructor?.user?.lastName}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {typeof course.trade === "object"
                        ? course.trade?.name || "-"
                        : "-"}
                    </td>
                    <td className="px-6 py-4">{getLevelBadge(course.level)}</td>
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">
                      {formatPrice(course.price, course.isFree)}
                      {course.discountPrice &&
                        course.discountPrice < course.price && (
                          <span className="ml-1 text-xs text-green-600">
                            (${course.discountPrice})
                          </span>
                        )}
                    </td>
                    <td className="px-6 py-4 text-sm">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-gray-900 font-medium">
                          {course.totalModules || 0} modules
                        </span>
                        <span className="text-gray-500 text-xs">
                          {course.enrollmentCount || 0} enrolled
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-700">
                      {course.accessDurationDays ? (
                        <span>{course.accessDurationDays} Days</span>
                      ) : (
                        <span className="text-gray-500">Lifetime</span>
                      )}
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(course.status, course.isPublished)}
                    </td>
                    <td className="px-6 py-4 text-[#989797]">
                      {formatDate(course.createdAt)}
                    </td>
                    <td className="px-6 py-4 text-right relative">
                      <button
                        className="p-1 hover:bg-gray-100 rounded-lg text-gray-400 hover:text-gray-600 transition-colors"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeMenu?.id === course._id) {
                            setActiveMenu(null);
                          } else {
                            const rect =
                              e.currentTarget.getBoundingClientRect();
                            setActiveMenu({
                              id: course._id,
                              position: {
                                top: rect.bottom,
                                right: window.innerWidth - rect.right,
                              },
                            });
                          }
                        }}
                      >
                        <MoreHorizontal size={20} />
                      </button>

                      <TableActionMenu
                        isOpen={activeMenu?.id === course._id}
                        onClose={() => setActiveMenu(null)}
                        position={activeMenu?.position || null}
                      >
                        <button
                          onClick={() => {
                            onViewClick(course);
                            setActiveMenu(null);
                          }}
                          className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                        >
                          <Eye size={14} className="text-blue-500" />
                          View Details
                        </button>
                        {hasPermission('courses:manage-content') && (
                          <button
                            onClick={() => {
                              onManageStructure(course);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Layers size={14} className="text-purple-500" />
                            Manage Structure
                          </button>
                        )}
                        <div className="h-px bg-gray-100 my-1" />
                        {hasPermission('courses:update') && (
                          <button
                            onClick={() => {
                              onEditClick(course);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            <Edit2 size={14} className="text-blue-500" />
                            Edit Course
                          </button>
                        )}
                        {onTogglePublish && hasPermission('courses:publish') && (
                          <button
                            onClick={() => {
                              onTogglePublish(course);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors"
                          >
                            {course.isPublished ? (
                              <>
                                <XCircle
                                  size={14}
                                  className="text-yellow-500"
                                />
                                Unpublish
                              </>
                            ) : (
                              <>
                                <CheckCircle2
                                  size={14}
                                  className="text-green-500"
                                />
                                Publish
                              </>
                            )}
                          </button>
                        )}
                        {hasPermission('courses:delete') && (
                          <button
                            onClick={() => {
                              onDeleteClick(course);
                              setActiveMenu(null);
                            }}
                            className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
                          >
                            <Trash2 size={14} />
                            Delete Course
                          </button>
                        )}
                      </TableActionMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Server-side Pagination UI */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between gap-2 text-sm text-gray-500">
          <span>
            {totalItems > 0 ? (
              <>
                Showing {Math.min((currentPage - 1) * pageSize + 1, totalItems)}{" "}
                to {Math.min(currentPage * pageSize, totalItems)} of{" "}
                {totalItems} courses
              </>
            ) : (
              "No courses found"
            )}
          </span>
          <div className="flex gap-1 ml-4">
            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === 1}
              onClick={() => onPageChange(currentPage - 1)}
            >
              &lt; Previous
            </button>

            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let p = i + 1;
              // Center the current page if many pages
              if (totalPages > 5) {
                if (currentPage <= 3) {
                  p = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  p = totalPages - 4 + i;
                } else {
                  p = currentPage - 2 + i;
                }
              }

              if (p > totalPages) return null;

              return (
                <button
                  key={p}
                  onClick={() => onPageChange(p)}
                  className={`px-3 py-1 rounded ${
                    currentPage === p
                      ? "bg-gray-900 text-white"
                      : "hover:bg-gray-100"
                  }`}
                >
                  {p}
                </button>
              );
            })}

            <button
              className="px-2 py-1 hover:bg-gray-100 rounded disabled:opacity-50"
              disabled={currentPage === totalPages || totalItems === 0}
              onClick={() => onPageChange(currentPage + 1)}
            >
              Next &gt;
            </button>
          </div>
        </div>
      </Card>
    </div>
  );
}
